import { CardBody, Col, Container, Row, Label, Input } from "reactstrap";
import Avatar from "@src/@core/components/ui-components/avatar";
import Breadcrumbs from "@src/@core/components/ui-components/breadcrumbs";
import Card from "@src/@core/components/ui-components/card";
import CommonModal from "@components/ui-components/modal";
import { ComponentSpinner } from "@src/@core/components/spinner";
import CopyToClipboard from "@src/@core/components/ui-components/copy-to-clipboard";
import { DATE_FORMAT_VIEW } from "@src/utility/constants";
import Flex from "@src/@core/components/ui-components/flex";
import FormView from "@components/views/form-view";
import InputField from "@components/fields/input-field";
import LoadingButton from "@components/ui-components/loading-button";
import LongNameFormatter from "@src/@core/components/formatters/long-name-formatter";
import { ProductContext } from "@src/utility/context/product";
import { UserContext } from "@src/utility/context/user";
import { getStatusBadge } from "@src/utility/helpers/utils";
import productBanner from "@src/assets/images/product-banner.png";
import size from "lodash/size";
import { useContext } from "react";
import useDateHandler from "@hooks/use-date-handler";
import { useNavigate } from "react-router-dom";
import usePlatforms from "./use-platforms";
import { useTranslation } from "react-i18next";
import InputPasswordToggle from "@src/@core/components/fields/input-password-toggle";
import themeConfig from "@src/configs/theme-config";
const Platforms = () => {
    const { t } = useTranslation();
    const { dateFormatter } = useDateHandler();
    const navigate = useNavigate();

    const {
        data: { platforms = [] },
        loading,
        methods,
        modalConfig,
        onSubmit,
        updateLoading,
        handleModalConfig,
        handleRowActions,
        isOpen,
        isDirty,
        productKeyModal,
        handleProductKeyModalConfig,
        handleSettingsClick,
        handleRegenerateProductKey,
        productKeyLoading
    } = usePlatforms();

    const { userData: { user: { name, role } = {} } = {} } = useContext(UserContext);

    const { handleProductSelect } = useContext(ProductContext);
    const {
        control,
        formState: { errors }
    } = methods;
    const isEdit = !!size(modalConfig.row._id);

    const handleNavigation = (product) => {
        handleProductSelect(product);
        navigate(`/dashboard/${product._id}`);
    };

    const content = [
        {
            leftChild: {
                title: "Enter Basic Details",
                desc : "Enter the basic details of the platform which you wish to create"
            },
            rightChild: (
                <>
                    <InputField
                        errors={errors}
                        id="name"
                        label="Name"
                        name="name"
                        required
                        control={control}/>
                    <InputField
                        errors={errors}
                        id="itcat"
                        label="Intercom Access Token"
                        name="itcat"
                        control={control}/>
                </>
            )
        }
    ];

    const ProductKeyModalContent = () => {
        if (productKeyLoading || !productKeyModal.data) {
            return (
                <div className="text-center py-4">
                    <ComponentSpinner />
                </div>
            );
        }

        const { product_key, metadata } = productKeyModal.data;

        return (
            <div>
                <div className="mb-1">
                    <label className="form-label fw-bold">{t("Product Key")}</label>
                    <Flex justify="between" align="center">
                        <InputPasswordToggle
                            name="apikey"
                            value={product_key}
                            readOnly
                            htmlFor="product-key-input"
                            placeholder={t("Product Key")}
                            className="w-100"/>
                        <CopyToClipboard
                            text={product_key}
                            tooltipContent="Copy product key"
                            id="copy-product-key"
                            testId="copy-product-key-btn"/>
                    </Flex>
                    <LoadingButton
                        color="primary"
                        text="Regenerate Key"
                        className="mt-1"
                        icon="refresh"
                        onClick={handleRegenerateProductKey}
                        loading={productKeyModal.regenerating}
                        testId="regenerate-key-btn"/>
                </div>
                <div className="mb-4">
                    <Label className="form-label fw-bold">{t("cURL")}</Label>
                    <Flex>
                        <Input
                            type="textarea"
                            className="w-100"
                            rows="8"
                            value={metadata?.curl || ""}
                            readOnly/>
                        <CopyToClipboard
                            text={metadata?.curl || ""}
                            tooltipContent="Copy cURL command"
                            id="copy-curl-command"
                            testId="copy-curl-command-btn"/>
                    </Flex>
                </div>
            </div>
        );
    };

    const ProductCard = ({ product }) => (
        <Col xl={3} lg={4} md={6} sm={12} className="mb-1">
            <div
                className="h-100 cursor-pointer"
                onClick={() => handleNavigation(product)}>
                <Card
                    className="h-100 shadow-sm border-0"
                    style={{ borderRadius: "12px" }}>
                    <CardBody className="p-0">
                        <Flex align="center">
                            <Avatar
                                color={getStatusBadge(product.name).color}
                                img={false}
                                content={product.name}
                                initials/>
                            <div className="flex-grow-1">
                                <LongNameFormatter
                                    content={product.name}
                                    className="fw-bold fs-3 text-primary"
                                    id={`name-${product._id}`}
                                    max={35}/>
                            </div>
                            <div>
                                <i 
                                    className="mdi mdi-cog text-secondary cursor-pointer"
                                    style={{ fontSize: "18px" }}
                                    onClick={(e) => handleSettingsClick(product, e)}
                                    title="Settings"></i>
                            </div>
                        </Flex>

                        <Flex className="my-1">
                            <h6>ID: {product._id}</h6>
                            <CopyToClipboard
                                text={product._id}
                                tooltipContent="Copy to clipboard"
                                id="copy-product-id"
                                testId="copy-product-id-btn"/>
                        </Flex>

                        <Flex className="pb-1 justify-content-between">
                            <Flex className="fs-6">
                                <i className="mdi mdi-calendar"></i>
                                {dateFormatter({
                                    date    : product?.created_at,
                                    formatAs: DATE_FORMAT_VIEW
                                })}
                            </Flex>
                        </Flex>
                  
                        {!(role === "csm" || role === "sdr") && (
                            <div className="d-flex justify-content-end align-items-center pt-1 border-top">
                                <Flex className="gap-1">
                                    <LoadingButton
                                        icon="pencil"
                                        color="info"
                                        outline
                                        testId="platform-edit"
                                        id={`${product._id}-edit-button`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowActions(product, "edit");
                                        }}
                                        title="Edit"/>
                                    <LoadingButton
                                        icon="delete"
                                        color="danger"
                                        outline
                                        testId="platform-delete"
                                        id={`${product._id}-delete-button`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowActions(product, "delete");
                                        }}
                                        title="Delete"/>
                                </Flex>
                            </div>
                        )}

                    </CardBody>
                </Card>
            </div>
        </Col>
    );

    return (
        <>
            <Breadcrumbs
                items={[
                    {
                        title : "Products",
                        active: true
                    }
                ]}/>

            {loading ? (
                <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "300px" }}>
                    <ComponentSpinner />
                </div>
            ) : (
                <Container className="p-0">
                    <div className="mt-2 mb-1 p-1 rounded-3 border">
                        <Row className="align-items-center">
                            <Col md={2} className="text-center mb-3 mb-md-0">
                                <img src={productBanner} alt="" className="w-75 h-75" />
                            </Col>
                            <Col md={10}>
                                <h4 className="mb-2 text-primary">Hi, {name}</h4>
                                <p className="mb-0">
                                    {t(
                                        `Experience the future of customer success with ${themeConfig.app.name} all-in-one platform. From onboarding to retention, effortlessly manage reports, logs and integrations - simplifying every step of your customer success journey.`
                                    )}
                                </p>
                            </Col>
                        </Row>
                    </div>

                    <Flex className="justify-content-end">
                        <LoadingButton
                            color="primary"
                            icon="plus"
                            onClick={() => handleModalConfig({})}
                            subject="platforms"
                            text="Add Product"/>
                    </Flex>

                    {platforms.length > 0 ? (
                        <Row>
                            {platforms.map((product) => (
                                <ProductCard
                                    key={product._id}
                                    product={product}
                                    navigate={navigate}/>
                            ))}
                        </Row>
                    ) : (
                        <div className="text-center py-5">
                            <h4 className="text-muted">{t("No Products Found")}</h4>
                            <p className="text-muted">
                                {t("Get started by creating your first product")}
                            </p>
                        </div>
                    )}
                </Container>
            )}

            {/* Add/Edit Product Modal */}
            <CommonModal
                title={`${isEdit ? "Edit" : "Add"} Product`}
                body={<FormView content={content} />}
                confirmButton={{
                    txt    : isEdit ? "Update" : "Add",
                    icon   : "content-save",
                    onClick: onSubmit,
                    loading: updateLoading
                }}
                type="extraLarge"
                isDirty={isDirty}
                loading={loading}
                handleModal={handleModalConfig}
                isOpen={isOpen}/>

            {/* Product Key Settings Modal */}
            <CommonModal
                title="Product Settings"
                body={<ProductKeyModalContent />}
                type="large"
                handleModal={handleProductKeyModalConfig}
                isOpen={productKeyModal.isOpen}
                showConfirmButton={false}/>
        </>
    );
};

export default Platforms;